import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import HomePage from './pages/HomePage';
import SignPage from './pages/SignPage';
import HistoryPage from './pages/HistoryPage';
import UpgradePage from './pages/UpgradePage';
import PaymentPage from './pages/PaymentPage';
import ClickPaymentPage from './pages/ClickPaymentPage';
import StampsPage from './pages/StampsPage';
import PublicOfferPage from './pages/PublicOfferPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SavedCardPage from './pages/SavedCardPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminPlansPage from './pages/admin/AdminPlansPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import Header from './components/Header';
import SplashScreen from './components/SplashScreen';
import { TelegramProvider } from './context/TelegramContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import './App.css';

function AppContent() {
  const location = useLocation();
  
  // Показываем кнопку назад на страницах подписания, истории, подписки, оплаты, управления печатями, публичной оферты, политики конфиденциальности и способов оплаты
  const showBackButton = ['/sign', '/history', '/upgrade', '/payment', '/payment/click', '/stamps', '/offer', '/privacy', '/saved-card'].includes(location.pathname);
  
  // Прячем Header на админ-страницах
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <div className="app">
      {!isAdminPage && <Header showBackButton={showBackButton} />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/sign" element={<SignPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment/click" element={<ClickPaymentPage />} />
        <Route path="/stamps" element={<StampsPage />} />
        <Route path="/offer" element={<PublicOfferPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/saved-card" element={<SavedCardPage />} />
        
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route 
          path="/admin" 
          element={
            <AdminProtectedRoute>
              <AdminDashboardPage />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <AdminProtectedRoute>
              <AdminUsersPage />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/admin/subscriptions" 
          element={
            <AdminProtectedRoute>
              <AdminSubscriptionsPage />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/admin/plans" 
          element={
            <AdminProtectedRoute>
              <AdminPlansPage />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/admin/payments" 
          element={
            <AdminProtectedRoute>
              <AdminPaymentsPage />
            </AdminProtectedRoute>
          } 
        />
      </Routes>
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    // Функция для принудительной установки светлой темы
    const forceLightTheme = () => {
      // Принудительно устанавливаем светлые цвета для CSS переменных
      document.documentElement.style.setProperty('--tg-theme-bg-color', '#f5f7fa', 'important');
      document.documentElement.style.setProperty('--tg-theme-text-color', '#1a1f36', 'important');
      document.documentElement.style.setProperty('--tg-theme-button-color', '#0119ED', 'important');
      document.documentElement.style.setProperty('--tg-theme-button-text-color', '#ffffff', 'important');
      document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', '#ffffff', 'important');
      document.documentElement.style.setProperty('--tg-theme-hint-color', '#697386', 'important');
      document.documentElement.style.setProperty('--tg-theme-link-color', '#0119ED', 'important');
      document.documentElement.style.setProperty('--tg-theme-section-bg-color', '#ffffff', 'important');
      document.documentElement.style.setProperty('--tg-theme-section-header-text-color', '#697386', 'important');
      document.documentElement.style.setProperty('--tg-theme-subtitle-text-color', '#697386', 'important');
      document.documentElement.style.setProperty('--tg-theme-destructive-text-color', '#ff4444', 'important');
      document.documentElement.style.setProperty('--tg-theme-border-color', '#e3e8ef', 'important');
      
      // Устанавливаем фон для body и html
      document.body.style.backgroundColor = '#f5f7fa';
      document.body.style.color = '#1a1f36';
      document.documentElement.style.backgroundColor = '#f5f7fa';
      
      // Блокируем темную тему через color-scheme
      document.documentElement.style.colorScheme = 'light only';
      document.body.style.colorScheme = 'light only';
    };
    
    // Инициализация Telegram Web App
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.ready();
      tg.expand();
      
      // Устанавливаем явно светлую тему
      tg.setHeaderColor('#f5f7fa');
      tg.setBackgroundColor('#f5f7fa');
      
      // Блокируем изменение темы через Telegram
      if (tg.themeParams) {
        Object.keys(tg.themeParams).forEach(key => {
          tg.themeParams[key] = null;
        });
      }
    }
    
    // Принудительно устанавливаем светлую тему при загрузке
    forceLightTheme();
    
    // Следим за изменениями темы и принудительно возвращаем светлую
    const observer = new MutationObserver(() => {
      forceLightTheme();
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: false
    });
    
    // Переопределяем тему каждые 100мс для гарантии (первые 5 секунд)
    const intervals = [];
    for (let i = 0; i < 50; i++) {
      intervals.push(setTimeout(forceLightTheme, i * 100));
    }
    
    return () => {
      observer.disconnect();
      intervals.forEach(clearTimeout);
    };
  }, []);

  if (showSplash) {
    return <SplashScreen onFinish={() => {
      setShowSplash(false);
      // Небольшая задержка перед показом контента для плавности
      setTimeout(() => setIsAppReady(true), 50);
    }} />;
  }

  return (
    <Router>
      <AdminAuthProvider>
        <TelegramProvider>
          {showSplash && <SplashScreen />}
          <div className={`app-wrapper ${isAppReady ? 'app-ready' : ''}`}>
            <AppContent />
          </div>
        </TelegramProvider>
      </AdminAuthProvider>
    </Router>
  );
}

export default App;

